import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { fmtDate } from "@/lib/constants";
import { BP_EVIDENCE_TYPES, BP_OWNERS, BP_REVIEW_STATUSES, BP_REVIEW_STYLES, BP_DIMENSIONS, BP_TOOL_STATUSES, BP_CUSTOMER_SYSTEM_TYPES } from "@/lib/reconConstants";
import { Save, CheckCircle2, Archive, Trash2, ImageUp, ShieldAlert, Eye, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const Sec = ({ n, title, children }) => (
  <div className="border-t border-border pt-4">
    <div className="text-[10px] font-semibold uppercase tracking-[0.15em] text-gold-dark mb-2">Section {n} · {title}</div>
    {children}
  </div>
);
const Badge = ({ className, children }) => (<span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${className}`}>{children}</span>);

export function BlueprintDetail({ bid, create, onClose, caps, onChanged }) {
  const open = !!bid || !!create;
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [editing, setEditing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirms, setConfirms] = useState({});

  useEffect(() => {
    if (create) { setD(null); setForm({ evidence_type: "Other", expected_owner: "Client", applicable_dimensions: [] }); setEditing(true); return; }
    if (bid) { api.get(`/blueprints/${bid}`).then((r) => { setD(r.data); setForm(r.data); setEditing(false); }).catch(() => {}); }
  }, [bid, create]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDim = (dim) => { const arr = form.applicable_dimensions || []; set("applicable_dimensions", arr.includes(dim) ? arr.filter((x) => x !== dim) : [...arr, dim]); };

  const save = () => {
    if (create) {
      if (!form.control_number || !form.visual_description) return toast.error("Control number and visual description are required.");
      api.post("/blueprints", form).then(() => { toast.success("Blueprint created (Draft)."); onChanged(); onClose(); })
        .catch((e) => toast.error(e.response?.data?.detail || "Failed."));
    } else {
      api.put(`/blueprints/${bid}`, form).then((r) => { setD(r.data); setForm(r.data); setEditing(false); toast.success("Saved."); onChanged(); })
        .catch((e) => toast.error(e.response?.data?.detail || "Failed."));
    }
  };
  const CONFIRM_ITEMS = [
    ["capability_not_product", "Describes the capability rather than requiring a specific product"],
    ["pattern_neutral", "The evidence pattern is vendor-neutral"],
    ["ms_accurate", "The Microsoft-native option is accurate"],
    ["equivalents_allowed", "Equivalent customer systems are allowed where appropriate"],
    ["source_not_exposed", "The source product is not exposed as a requirement"],
    ["gov_license_not_overstated", "Government-cloud and licensing limitations are not overstated"],
    ["mappings_accurate", "Control and assessment-objective mappings remain accurate"],
  ];
  const doApprove = () => {
    if (d?.vendor_neutral_status !== "confirmed") { setConfirms({}); setShowConfirm(true); return; }
    setStatus("Approved Blueprint");
  };
  const submitConfirm = () => {
    if (!CONFIRM_ITEMS.every(([k]) => confirms[k])) return toast.error("All confirmations are required.");
    api.post(`/blueprints/${bid}/confirm-vendor-neutral`, { confirmations: confirms }).then((r) => {
      setD(r.data); setForm(r.data); setShowConfirm(false); toast.success("Vendor-neutral confirmed. You can now approve.");
      onChanged();
    }).catch((e) => toast.error(e.response?.data?.detail || "Failed."));
  };
  const setStatus = (status) => {
    api.post(`/blueprints/${bid}/status`, { review_status: status }).then((r) => { setD(r.data); setForm(r.data); toast.success(`Status → ${status}`); onChanged(); })
      .catch((e) => toast.error(e.response?.data?.detail || "Failed."));
  };
  const del = () => { api.delete(`/blueprints/${bid}`).then(() => { toast.success("Deleted."); onChanged(); onClose(); }).catch((e) => toast.error(e.response?.data?.detail || "Failed.")); };
  const uploadImg = (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("redaction_notes", "");
    api.post(`/blueprints/${bid}/reference-image`, fd, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => { setD(r.data); toast.success("Reference image uploaded (admin-only)."); }).catch((e) => toast.error(e.response?.data?.detail || "Upload failed."));
  };

  const view = d && !editing;
  const F = ({ label, k, textarea, rows = 2 }) => (
    <div className="space-y-1 mb-3">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {editing ? (textarea ? <Textarea rows={rows} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`bp-field-${k}`} />
        : <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`bp-field-${k}`} />)
        : <p className="text-sm text-navy whitespace-pre-wrap">{(d && d[k]) || "—"}</p>}
    </div>
  );

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="bp-detail-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-navy flex items-center gap-2 flex-wrap">
            {create ? "New Blueprint" : d?.title}
            {d && <Badge className={BP_REVIEW_STYLES[d.review_status]}>{d.review_status}</Badge>}
            {d?.needs_manual_review && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Needs manual review</Badge>}
          </SheetTitle>
          {d && <SheetDescription>{d.blueprint_code} · {d.control_number} · {d.control_family} · v{d.version}</SheetDescription>}
        </SheetHeader>

        <div className="mt-4 space-y-4 text-sm">
          {!create && (
            <div className="flex flex-wrap gap-2">
              {caps.create && !editing && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="bp-edit-btn">Edit</Button>}
              {editing && <Button size="sm" className="bg-gold text-navy hover:bg-gold-dark" onClick={save} data-testid="bp-save-btn"><Save className="h-4 w-4 mr-1" />Save</Button>}
              {caps.assign && d?.review_status !== "Approved Blueprint" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doApprove} data-testid="bp-approve-btn"><CheckCircle2 className="h-4 w-4 mr-1" />Approve</Button>}
              {caps.assign && <Select onValueChange={setStatus}><SelectTrigger className="w-[170px] h-8" data-testid="bp-status-select"><SelectValue placeholder="Set status…" /></SelectTrigger><SelectContent>{BP_REVIEW_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>}
              {caps.delete && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={del} data-testid="bp-delete-btn"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>}
            </div>
          )}

          <Sec n="1" title="Identification">
            {d?.needs_vendor_review && (
              <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="bp-vendor-review-banner">
                <ShieldAlert className="h-3.5 w-3.5 inline mr-1" /> This blueprint referenced a vendor-specific product and was set to <strong>Needs Re-Review</strong>. Confirm the vendor-neutral checklist before it can be approved.
              </div>
            )}
            {create && <F label="Blueprint title" k="title" />}
            <div className="grid grid-cols-2 gap-3">
              <F label="Control number (base)" k="control_number" />
              <F label="Assessment objective" k="assessment_objective" />
            </div>
            <F label="Framework" k="framework" />
            <F label="System / Portal" k="system_or_portal" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 mb-3"><Label className="text-xs text-muted-foreground">Evidence type</Label>
                {editing ? <Select value={form.evidence_type} onValueChange={(v) => set("evidence_type", v)}><SelectTrigger data-testid="bp-field-evidence_type"><SelectValue /></SelectTrigger><SelectContent>{BP_EVIDENCE_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select> : <p className="text-sm text-navy">{d?.evidence_type}</p>}</div>
              <div className="space-y-1 mb-3"><Label className="text-xs text-muted-foreground">Expected owner</Label>
                {editing ? <Select value={form.expected_owner} onValueChange={(v) => set("expected_owner", v)}><SelectTrigger data-testid="bp-field-expected_owner"><SelectValue /></SelectTrigger><SelectContent>{BP_OWNERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select> : <p className="text-sm text-navy">{d?.expected_owner}</p>}</div>
            </div>
          </Sec>

          <Sec n="1b" title="Vendor-Neutral Capability">
            <F label="Required capability" k="required_capability" textarea rows={2} />
            <F label="Vendor-neutral evidence pattern" k="vendor_neutral_pattern" textarea rows={2} />
            <div className="space-y-1 mb-3"><Label className="text-xs text-muted-foreground">Preferred Microsoft source</Label>
              <div className="flex flex-wrap gap-1.5">{(d?.preferred_microsoft_source || []).length ? d.preferred_microsoft_source.map((s, i) => <Badge key={i} className="bg-blue-50 text-blue-800 border-blue-200">{s}</Badge>) : <span className="text-xs text-muted-foreground">—</span>}</div></div>
            <div className="space-y-1 mb-3"><Label className="text-xs text-muted-foreground">Acceptable alternative system categories</Label>
              <div className="flex flex-wrap gap-1.5">{(d?.acceptable_alternative_system_categories || []).length ? d.acceptable_alternative_system_categories.map((s, i) => <Badge key={i} className="bg-slate-100 text-slate-700 border-slate-200">{s}</Badge>) : <span className="text-xs text-muted-foreground">—</span>}</div></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 mb-3"><Label className="text-xs text-muted-foreground">Tool selection status</Label>
                {editing ? <Select value={form.tool_selection_status} onValueChange={(v) => set("tool_selection_status", v)}><SelectTrigger data-testid="bp-field-tool_selection_status"><SelectValue /></SelectTrigger><SelectContent>{BP_TOOL_STATUSES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select> : <p className="text-sm text-navy">{d?.tool_selection_status}</p>}</div>
              <div className="space-y-1 mb-3"><Label className="text-xs text-muted-foreground">Customer system type</Label>
                {editing ? <Select value={form.customer_system_type || ""} onValueChange={(v) => set("customer_system_type", v)}><SelectTrigger data-testid="bp-field-customer_system_type"><SelectValue placeholder="—" /></SelectTrigger><SelectContent>{BP_CUSTOMER_SYSTEM_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select> : <p className="text-sm text-navy">{d?.customer_system_type || "—"}</p>}</div>
            </div>
            <F label="License dependency" k="license_dependency" />
            {d?.system_specific_notes && <p className="text-xs text-amber-700">{d.system_specific_notes}</p>}
            <div className="flex items-center gap-2 text-xs mt-1">
              <Badge className={d?.vendor_neutral_status === "confirmed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}>Vendor-neutral: {d?.vendor_neutral_status || "not_started"}</Badge>
              {d?.requires_cui_capable_platform && <Badge className="bg-red-100 text-red-800 border-red-200">Requires CUI-capable platform</Badge>}
            </div>
          </Sec>

          <Sec n="2" title="Visual Description"><F label="What the artifact should visibly show" k="visual_description" textarea rows={3} /></Sec>
          <Sec n="3" title="What It Proves"><F label="Implementation claim supported" k="what_it_proves" textarea rows={2} /></Sec>
          <Sec n="4" title="What It Does Not Prove"><F label="Limitations / additional proof still needed" k="what_it_does_not_prove" textarea rows={2} /></Sec>
          <Sec n="5" title="Collection Instructions">
            <F label="Navigation / collection steps" k="collection_instructions" textarea rows={2} />
            <F label="Redaction / neutralization guidance" k="neutralization_rule" textarea rows={2} />
            <F label="Recommended filename pattern" k="recommended_filename_pattern" />
            <F label="Suggested refresh frequency" k="suggested_refresh_frequency" />
          </Sec>
          <Sec n="6" title="Alternatives">
            <F label="Acceptable alternative evidence" k="acceptable_alternative_evidence" textarea rows={2} />
            <F label="Additional evidence recommended" k="additional_evidence_recommended" textarea rows={2} />
            <F label="Common rejection reasons" k="common_rejection_reasons" textarea rows={2} />
          </Sec>
          <Sec n="7" title="Implementation Dimensions">
            {editing ? (
              <div className="flex flex-wrap gap-2">{BP_DIMENSIONS.map((dim) => (
                <label key={dim} className="flex items-center gap-1.5 text-xs cursor-pointer border rounded px-2 py-1"><input type="checkbox" checked={(form.applicable_dimensions || []).includes(dim)} onChange={() => toggleDim(dim)} />{dim}</label>))}</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">{(d?.applicable_dimensions || []).length ? d.applicable_dimensions.map((dim) => <Badge key={dim} className="bg-navy/10 text-navy border-navy/20">{dim}</Badge>) : <span className="text-muted-foreground text-xs">None specified</span>}</div>
            )}
          </Sec>

          {!create && (
            <Sec n="8" title="Internal Review">
              <F label="Internal reviewer notes" k="internal_reviewer_notes" textarea rows={2} />
              <p className="text-xs text-muted-foreground">Status: {d?.review_status} · Updated by {d?.updated_by} · {fmtDate(d?.updated_at)}</p>
              {d?.source_values && Object.keys(d.source_values).length > 0 && (
                <p className="text-[11px] text-amber-700 mt-1">Imported source values (mapped): {Object.entries(d.source_values).map(([k, v]) => `${k}="${v}"`).join(", ")}</p>
              )}
              {(d?.version_history || []).length > 0 && (
                <details className="mt-2 text-xs"><summary className="cursor-pointer text-gold-dark">Version history ({d.version_history.length})</summary>
                  {d.version_history.map((h, i) => <div key={i} className="text-muted-foreground py-0.5">v{h.version} · {h.by || "—"} · {fmtDate(h.at)}</div>)}
                </details>
              )}
              {/* Reference image */}
              <div className="mt-4 rounded-md border border-border p-3 bg-slate-50/60">
                <div className="flex items-center gap-2 text-xs font-semibold text-navy mb-2"><ShieldAlert className="h-4 w-4 text-amber-600" /> Sanitized Reference Image (admin-only)</div>
                {d?.reference_image ? (
                  <div className="space-y-2">
                    <a href={`${API}/blueprints/${bid}/reference-image`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs text-gold-dark underline" data-testid="bp-ref-image-view"><Eye className="h-3.5 w-3.5" /> View reference image</a>
                    <p className="text-[11px] font-semibold text-red-700">Reference Example — Do Not Submit as Customer Evidence</p>
                    <p className="text-[11px] text-muted-foreground">{d.reference_image.original_filename} · uploaded by {d.reference_image.uploaded_by}</p>
                  </div>
                ) : <p className="text-xs text-muted-foreground">No reference image attached (optional).</p>}
                {caps.upload && (
                  <label className="mt-2 inline-flex items-center gap-1.5 text-xs cursor-pointer text-navy border border-dashed border-border rounded px-2 py-1 hover:bg-white" data-testid="bp-ref-image-upload-label">
                    <ImageUp className="h-3.5 w-3.5" /> {d?.reference_image ? "Replace image" : "Upload sanitized image"}
                    <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" data-testid="bp-ref-image-input" onChange={(e) => uploadImg(e.target.files[0])} />
                  </label>
                )}
              </div>
            </Sec>
          )}

          {create && <Button className="w-full bg-gold text-navy hover:bg-gold-dark" onClick={save} data-testid="bp-create-submit"><Save className="h-4 w-4 mr-1.5" /> Create Blueprint</Button>}
        </div>

        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent data-testid="bp-vendor-confirm-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" /> Confirm Vendor-Neutral Conversion</DialogTitle>
              <DialogDescription>All items must be confirmed before this blueprint can be approved.</DialogDescription></DialogHeader>
            <div className="space-y-2.5 py-2">
              {CONFIRM_ITEMS.map(([k, label]) => (
                <label key={k} className="flex items-start gap-2 text-sm cursor-pointer">
                  <Checkbox checked={!!confirms[k]} onCheckedChange={(v) => setConfirms((c) => ({ ...c, [k]: !!v }))} data-testid={`bp-confirm-${k}`} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitConfirm} data-testid="bp-confirm-submit">Confirm Vendor-Neutral</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}
